import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthContext';
import logger from '../../utils/logger';
import { formatDate, removeTrackingPixel } from './mailHubUtils';
import { useMailFilters } from './useMailFilters';
import { useMailCompose } from './useMailCompose';
import { useMailFolders } from './useMailFolders';
import { useMailLinks } from './useMailLinks';
import { useMailAttachments } from './useMailAttachments';
import { useMailScheduled } from './useMailScheduled';
import { useMailSignatures } from './useMailSignatures';
import { useMailActions } from './useMailActions';

export function useMailHub({ backendUrl, authToken, onNavigateToClient, onNavigateToCourtier, onNavigateToCompany, onNavigateToAssisteur, onNavigateToAssistanceClient, filterClientId = null, initialCompose = null, initialEmailId = null, initialFolder = 'inbox', onEmailOpened = null, onMinimize = null }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(initialFolder || 'inbox');
  const [clientInfo, setClientInfo] = useState(null);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailMenu, setShowEmailMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [accountStats, setAccountStats] = useState({ total: 0, read: 0, unread: 0, unlinked: 0 });
  
  // États pour les dossiers personnalisés
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  
  // États pour l'éditeur riche
  
  // Configuration Quill
  
  
  // Fonctions éditeur riche
  
  const ITEMS_PER_PAGE = 10;
  
  // États pour la pagination des emails
  const [emailsPage, setEmailsPage] = useState(1);
  const [emailsPerPage, setEmailsPerPage] = useState(20);
  const [totalEmails, setTotalEmails] = useState(0);

  // Filtres & recherche (sous-hook dédié)
  const {
    searchQuery, setSearchQuery,
    showFilters, setShowFilters,
    filters,
    resetFilters, updateFilters, hasActiveFilters
  } = useMailFilters({ setEmailsPage });

  // Signatures email (sous-hook dédié) — utilisées par la composition
  const {
    loadSignatures, getSignatureForAccount, generateSignatureHTML
  } = useMailSignatures({ backendUrl, authToken });
  
  // États pour les emails programmés
  
  // États pour les signatures
  
  // États pour les emails inconnus (non reconnus dans CRM)
  const [unknownEmails, setUnknownEmails] = useState(new Set());
  
  // Formulaire création client rapide
  
  // Formulaire création courtier rapide
  
  // Formulaire création contact annuaire rapide
  
  // Liste des compagnies pour l'annuaire

  // Fermer le menu email quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showEmailMenu && !e.target.closest('.email-menu-container')) {
        setShowEmailMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showEmailMenu]);

  useEffect(() => {
    loadEmailAccounts();
    loadSignatures();
    if (filterClientId) {
      loadClientInfo();
    }
  }, [filterClientId]);


  // Ouvrir automatiquement un email spécifique si initialEmailId est fourni
  useEffect(() => {
    const openEmailById = async () => {
      if (initialEmailId && selectedAccount) {
        try {
          // Charger les emails du bon dossier
          setCurrentFolder(initialFolder);
          
          // Récupérer l'email spécifique
          const response = await fetch(`${backendUrl}/api/admin/mail/inbox/${initialEmailId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          
          if (response.ok) {
            const emailData = await response.json();
            setSelectedEmail(emailData);
            
            // Callback pour indiquer que l'email a été ouvert
            if (onEmailOpened) {
              onEmailOpened();
            }
          }
        } catch (error) {
          logger.error('Erreur lors de l\'ouverture de l\'email:', error);
        }
      }
    };
    
    openEmailById();
  }, [initialEmailId, selectedAccount, initialFolder, backendUrl, authToken, onEmailOpened]);



  // Note: Les notifications d'accusés de réception sont maintenant gérées par NotificationCenter.js

  const loadEmailAccounts = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/accounts`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const activeAccounts = data.filter(acc => acc.is_active);
        setEmailAccounts(activeAccounts);
        
        if (activeAccounts.length > 0 && !selectedAccount) {
          setSelectedAccount(activeAccounts[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading email accounts:', error);
      showError('Erreur lors du chargement des comptes email');
    }
  };

  const loadClientInfo = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/clients/${filterClientId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientInfo(data);
      }
    } catch (error) {
      logger.error('Error loading client info:', error);
    }
  };

  const loadEmails = React.useCallback(async () => {
    if (!selectedAccount) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        account_id: selectedAccount.id,
        page: emailsPage,
        limit: emailsPerPage
      });

      // Vue "Traités" : paramètre dédié, pas de filtre folder
      if (currentFolder === 'processed') {
        params.append('processed_view', 'true');
      } else {
        params.append('folder', currentFolder);
      }

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      // Filtre par client si actif
      if (filterClientId) {
        params.append('client_id', filterClientId);
      }

      // Appliquer les filtres
      if (filters.readStatus === 'read') {
        params.append('is_read', 'true');
      } else if (filters.readStatus === 'unread') {
        params.append('is_read', 'false');
      }

      if (filters.dateFrom) {
        params.append('date_from', filters.dateFrom);
      }

      if (filters.dateTo) {
        params.append('date_to', filters.dateTo);
      }

      if (filters.hasAttachments) {
        params.append('has_attachments', 'true');
      }

      if (filters.isImportant) {
        params.append('is_important', 'true');
      }

      if (filters.linkedToCRM) {
        params.append('linked_only', 'true');
      }

      if (filters.notLinkedToCRM) {
        params.append('unlinked_only', 'true');
      }

      if (filters.includeArchived) {
        params.append('include_archived', 'true');
      }

      // Filtre par dossier personnalisé
      if (selectedFolderId) {
        params.append('folder_id', selectedFolderId);
      }

      const response = await fetch(`${backendUrl}/api/admin/mail/inbox?${params}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        let data = await response.json();
        
        // En vue "processed", le backend filtre déjà correctement
        // (is_processed OR linked_*_id) — pas de filtre client-side nécessaire
        
        setEmails(data);
        
        // Compter les non lus
        const unread = data.filter(e => !e.is_read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      logger.error('Error loading emails:', error);
      showError('Erreur lors du chargement des emails');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, currentFolder, emailsPage, emailsPerPage, searchQuery, filters, selectedFolderId, backendUrl, authToken]);

  // Charger les statistiques du compte email sélectionné
  const loadAccountStats = React.useCallback(async () => {
    if (!selectedAccount) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/account-stats/${selectedAccount.id}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccountStats({
          total: data.total || 0,
          read: data.read || 0,
          unread: data.unread || 0,
          unlinked: data.unlinked || 0
        });
      }
    } catch (error) {
      logger.error('Error loading account stats:', error);
    }
  }, [selectedAccount, backendUrl, authToken]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 5000);
  };

  // Pièces jointes : téléchargement, ouverture, prévisualisation (sous-hook dédié)
  const {
    previewModal, setPreviewModal,
    showCRMAttachmentPicker, setShowCRMAttachmentPicker,
    showSaveAttachmentModal, setShowSaveAttachmentModal,
    attachmentToSave, setAttachmentToSave,
    downloadAttachment, openAttachmentInNewTab, previewAttachment, closePreviewModal, renderPreviewContent
  } = useMailAttachments({ backendUrl, authToken, showSuccess, showError });

  // Emails programmés (sous-hook dédié)
  const {
    scheduledEmails, scheduledEmailsLoading, loadScheduledEmails, cancelScheduledEmail
  } = useMailScheduled({ backendUrl, authToken, showSuccess, showError });

  // Composition d'email (sous-hook dédié)
  const {
    attachments, closeComposeModal, composeForm, composeModalDragging, composeModalMinimized, composeModalPosition,
    composeModalRef, handleCRMAttachments, handleDragEnter, handleDragLeave, handleDragOver, handleDrop,
    handleFileSelect, handleImageInsert, imageInputRef, insertEmoji, insertTable, isDragging,
    openComposeInNewWindow, openComposeModal, quillFormats, quillModules, quillRef, removeAttachment,
    replyMode, sendEmail, setComposeForm, setComposeModalDragOffset, setComposeModalDragging, setComposeModalMinimized,
    setReplyMode, setShowComposeModal, setShowEmojiPicker, setShowTableDialog, setTableCols, setTableRows,
    showComposeModal, showEmojiPicker, showTableDialog, tableCols, tableRows,
    minimizedWindows, minimizeCurrentWindow, restoreMinimizedWindow, closeMinimizedWindow, updateMinimizedTabPosition
  } = useMailCompose({
    authToken, backendUrl, currentFolder, emailAccounts, generateSignatureHTML,
    getSignatureForAccount, initialCompose, loadEmails, selectedAccount, showError, showSuccess,
    onMinimize
  });

  // Dossiers personnalisés + drag & drop (sous-hook dédié)
  const {
    emailFolders,
    showCreateFolderModal, setShowCreateFolderModal,
    newFolderName, setNewFolderName,
    draggedEmailId, dragOverFolderId,
    folderMenuOpen, setFolderMenuOpen,
    showRenameFolderModal, setShowRenameFolderModal,
    folderToRename, setFolderToRename,
    renameFolderName, setRenameFolderName,
    loadEmailFolders, createFolder, renameFolder, deleteFolder, openRenameModal,
    moveEmailToFolder,
    handleDragStart, handleEmailDragEnd,
    handleFolderDragOver, handleFolderDragLeave, handleFolderDrop, changeFolder
  } = useMailFolders({
    selectedAccount, backendUrl, authToken,
    loadEmails, loadAccountStats,
    setCurrentFolder, setEmailsPage,
    selectedFolderId, setSelectedFolderId
  });

  // Liaison CRM, annuaire de contacts & création rapide (sous-hook dédié)
  const {
    showLinkModal, setShowLinkModal,
    showEmailLinkManager, setShowEmailLinkManager,
    emailToLink, setEmailToLink,
    linkSuggestions, linkSearchQuery, setLinkSearchQuery, linkSearchResults,
    selectedLinks,
    showContactPicker, setShowContactPicker,
    contactPickerField, contactSearchQuery, setContactSearchQuery, contactResults,
    activeContactTab, setActiveContactTab,
    showCourtierClients, setShowCourtierClients, selectedCourtier, courtierClients,
    showCompanyContacts, setShowCompanyContacts, selectedCompany, companyContacts,
    currentPage, setKnownEmailsInfo,
    showUnknownContactModal, setShowUnknownContactModal,
    unknownContactEmail, unknownContactStep, setUnknownContactStep,
    unknownContactType, setUnknownContactType, creatingContact,
    quickClientForm, setQuickClientForm,
    quickCourtierForm, setQuickCourtierForm,
    quickAnnuaireForm, setQuickAnnuaireForm,
    companiesList,
    openUnknownContactModal, createQuickClient, createQuickCourtier, createQuickAnnuaireContact,
    searchEntitiesForLink, addToSelectedLinks, removeFromSelectedLinks, saveLinkAndProcess,
    openContactPicker, searchContacts, selectContactEmail, isEmailSelected, getSelectedCountByCategory,
    openCourtierClients, selectAllCourtierClients, deselectAllCourtierClients,
    openCompanyContacts, selectAllCompanyContacts, deselectAllCompanyContacts,
    getUniqueCompanies, paginateItems, renderPagination,
    selectAllFromTab, deselectAllFromTab
  } = useMailLinks({
    ITEMS_PER_PAGE, authToken, backendUrl, composeForm, emails, loadEmails, selectedEmail,
    setComposeForm, setEmails, setSelectedEmail, setUnknownEmails, showError, showSuccess
  });


  // Charger les stats quand le compte change
  useEffect(() => {
    if (selectedAccount) {
      loadAccountStats();
      loadEmailFolders();
    }
  }, [selectedAccount, loadAccountStats, loadEmailFolders]);

  useEffect(() => {
    if (currentFolder === 'scheduled') {
      loadScheduledEmails();
    } else {
      loadEmails();
    }
  }, [loadEmails, currentFolder, selectedFolderId]);

  // Vérifier les emails inconnus après chargement
  const checkUnknownEmails = React.useCallback(async (emailsList) => {
    if (!emailsList || emailsList.length === 0) return;
    
    // Extraire toutes les adresses email uniques des expéditeurs (pour les emails reçus)
    const senderEmails = [...new Set(
      emailsList
        .filter(e => e.direction === 'inbound' && e.from_address)
        .map(e => e.from_address.toLowerCase())
    )];
    
    if (senderEmails.length === 0) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/check-known-emails`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(senderEmails)
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnknownEmails(new Set(data.unknown_emails || []));
        setKnownEmailsInfo(data.known_emails || {});
      }
    } catch (error) {
      logger.error('Error checking unknown emails:', error);
    }
  }, [backendUrl, authToken]);
  
  // Vérifier les emails inconnus à chaque changement de la liste
  useEffect(() => {
    if (emails.length > 0) {
      checkUnknownEmails(emails);
    }
  }, [emails, checkUnknownEmails]);
  
  // Charger la liste des compagnies pour l'annuaire

  // Vérifier s'il y a des données pré-remplies (depuis la fiche client ou Hub Devis)
  useEffect(() => {
    const prefillTo = localStorage.getItem('mail_prefill_to');
    const prefillSubject = localStorage.getItem('mail_prefill_subject');
    const prefillBody = localStorage.getItem('mail_prefill_body');
    
    if ((prefillTo || prefillSubject || prefillBody) && selectedAccount) {
      // Ouvrir le modal de composition avec les données pré-remplies
      openComposeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // Actions sur les emails (sync, sélection, important, spam, suppression, masse) — sous-hook dédié
  const {
    selectedEmailIds,
    syncing, trackingStats,
    syncEmails, selectEmail, navigateEmail, hasPreviousEmail, hasNextEmail,
    toggleImportant, processEmail, processBulkEmails, deleteEmail,
    toggleEmailSelection, toggleSelectAll, bulkDelete, emptyFolder,
    markAsSpam, unmarkSpam, bulkUnmarkSpam, bulkMarkAsUnread, bulkMarkAsRead, bulkMarkAsSpam,
    handleCRMLink
  } = useMailActions({
    backendUrl, authToken, selectedAccount,
    emails, setEmails, selectedEmail, setSelectedEmail, setUnreadCount,
    currentFolder, loadEmails, showSuccess, showError,
    onNavigateToClient, onNavigateToCourtier, onNavigateToCompany, onNavigateToAssisteur, onNavigateToAssistanceClient
  });




  return {
    accountStats, activeContactTab, addToSelectedLinks, attachmentToSave, attachments, bulkDelete, bulkMarkAsRead, bulkMarkAsSpam,
    bulkMarkAsUnread, bulkUnmarkSpam, cancelScheduledEmail, changeFolder, clientInfo, closeComposeModal, closePreviewModal, companiesList,
    companyContacts, composeForm, composeModalDragging, composeModalMinimized, composeModalPosition, composeModalRef, contactPickerField, contactResults,
    contactSearchQuery, courtierClients, createFolder, createQuickAnnuaireContact, createQuickClient, createQuickCourtier, creatingContact, currentFolder,
    currentPage, deleteEmail, deleteFolder, deselectAllCompanyContacts, deselectAllCourtierClients, deselectAllFromTab, downloadAttachment, dragOverFolderId,
    draggedEmailId, emailAccounts, emailFolders, emailToLink, emails, emailsPage, emailsPerPage, errorMessage, emptyFolder,
    filters, folderMenuOpen, folderToRename, formatDate, getSelectedCountByCategory, getUniqueCompanies, handleCRMAttachments, handleCRMLink,
    handleDragEnter, handleDragLeave, handleDragOver, handleDragStart, handleDrop, handleEmailDragEnd, handleFileSelect, handleFolderDragLeave,
    handleFolderDragOver, handleFolderDrop, handleImageInsert, hasActiveFilters, hasNextEmail, hasPreviousEmail, imageInputRef, insertEmoji,
    insertTable, isDragging, isEmailSelected, linkSearchQuery, linkSearchResults, linkSuggestions, loadEmails, loading,
    markAsSpam, navigateEmail, newFolderName, moveEmailToFolder, openAttachmentInNewTab, openCompanyContacts, openComposeInNewWindow, openComposeModal, openContactPicker,
    openCourtierClients, openRenameModal, openUnknownContactModal, paginateItems, previewAttachment, previewModal, processBulkEmails, processEmail,
    quickAnnuaireForm, quickClientForm, quickCourtierForm, quillFormats, quillModules, quillRef, removeAttachment,
    removeFromSelectedLinks, removeTrackingPixel, renameFolder, renameFolderName, renderPagination, renderPreviewContent, replyMode, resetFilters,
    saveLinkAndProcess, scheduledEmails, scheduledEmailsLoading, searchContacts, searchEntitiesForLink, searchQuery, selectAllCompanyContacts, selectAllCourtierClients,
    selectAllFromTab, selectContactEmail, selectEmail, selectedAccount, selectedCompany, selectedCourtier, selectedEmail, selectedEmailIds,
    selectedFolderId, selectedLinks, sendEmail, setActiveContactTab, setAttachmentToSave, setComposeForm, setComposeModalDragOffset, setComposeModalDragging,
    setComposeModalMinimized, setContactSearchQuery, setCurrentFolder, setEmailToLink, setEmailsPage, setEmailsPerPage, setFolderMenuOpen, setFolderToRename,
    setLinkSearchQuery, setNewFolderName, setPreviewModal, setQuickAnnuaireForm, setQuickClientForm, setQuickCourtierForm, setRenameFolderName, setReplyMode,
    setSearchQuery, setSelectedAccount, setSelectedFolderId, setShowCRMAttachmentPicker, setShowCompanyContacts, setShowComposeModal, setShowContactPicker, setShowCourtierClients,
    setShowCreateFolderModal, setShowEmailLinkManager, setShowEmailMenu, setShowEmojiPicker, setShowFilters, setShowLinkModal, setShowRenameFolderModal, setShowSaveAttachmentModal,
    setShowTableDialog, setShowUnknownContactModal, setTableCols, setTableRows, setUnknownContactStep, setUnknownContactType, showCRMAttachmentPicker, showCompanyContacts,
    showComposeModal, showContactPicker, showCourtierClients, showCreateFolderModal, showEmailLinkManager, showEmailMenu, showEmojiPicker, showFilters,
    showLinkModal, showRenameFolderModal, showSaveAttachmentModal, showSuccess, showTableDialog, showUnknownContactModal, successMessage, syncEmails,
    syncing, tableCols, tableRows, toggleEmailSelection, toggleImportant, toggleSelectAll, trackingStats, unknownContactEmail,
    unknownContactStep, unknownContactType, unknownEmails, unmarkSpam, unreadCount, updateFilters,
    minimizedWindows, minimizeCurrentWindow, restoreMinimizedWindow, closeMinimizedWindow, updateMinimizedTabPosition
  };
}
